import { useState } from 'react';
import { Lock, Unlock, AlertTriangle } from 'lucide-react';

interface UnlockProjectDialogProps {
  projectName: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export function UnlockProjectDialog({
  projectName,
  onConfirm,
  onCancel,
}: UnlockProjectDialogProps) {
  const [confirmText, setConfirmText] = useState('');
  const [holdProgress, setHoldProgress] = useState(0);
  const [isHolding, setIsHolding] = useState(false);

  const requiredText = 'UNLOCK';
  const isTextMatch = confirmText.toUpperCase() === requiredText;

  let holdTimer: NodeJS.Timeout | null = null;
  let progressInterval: NodeJS.Timeout | null = null;

  const startHold = () => {
    if (!isTextMatch) return;

    setIsHolding(true);
    setHoldProgress(0);

    const startTime = Date.now();
    const holdDuration = 3000;

    progressInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min((elapsed / holdDuration) * 100, 100);
      setHoldProgress(progress);

      if (progress >= 100) {
        clearInterval(progressInterval!);
      }
    }, 50);

    holdTimer = setTimeout(() => {
      onConfirm();
    }, holdDuration);
  };

  const cancelHold = () => {
    if (holdTimer) clearTimeout(holdTimer);
    if (progressInterval) clearInterval(progressInterval);
    setIsHolding(false);
    setHoldProgress(0);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center space-x-2">
            <AlertTriangle className="w-6 h-6 text-orange-600" />
            <span>Unlock Completed Project</span>
          </h2>
        </div>

        <div className="bg-orange-50 border border-orange-200 rounded-md p-4 mb-4">
          <p className="text-sm text-orange-900 mb-2">
            <strong>Warning:</strong> This project has been marked as complete.
            Unlocking it will allow editing but may compromise data integrity.
          </p>
          <p className="text-sm text-orange-800">
            Project: <strong>{projectName}</strong>
          </p>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Type <span className="font-bold text-red-600">{requiredText}</span> to enable unlock button:
          </label>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 uppercase"
            placeholder="Type UNLOCK"
            autoFocus
          />
        </div>

        <div className="space-y-3">
          <button
            onMouseDown={startHold}
            onMouseUp={cancelHold}
            onMouseLeave={cancelHold}
            onTouchStart={startHold}
            onTouchEnd={cancelHold}
            disabled={!isTextMatch}
            className={`w-full relative overflow-hidden py-3 px-4 rounded-md font-medium transition-all ${
              isTextMatch
                ? 'bg-orange-600 text-white hover:bg-orange-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            <div
              className="absolute inset-0 bg-orange-800 transition-all"
              style={{
                width: `${holdProgress}%`,
                opacity: 0.5,
              }}
            />
            <div className="relative flex items-center justify-center space-x-2">
              {isHolding ? (
                <>
                  <Lock className="w-5 h-5" />
                  <span>Hold to Unlock... ({Math.floor(holdProgress)}%)</span>
                </>
              ) : (
                <>
                  <Unlock className="w-5 h-5" />
                  <span>Hold for 3 seconds to Unlock</span>
                </>
              )}
            </div>
          </button>

          <button
            onClick={onCancel}
            className="w-full py-2 px-4 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
        </div>

        <p className="text-xs text-gray-500 mt-4 text-center">
          Unlocking will change the project status back to Active
        </p>
      </div>
    </div>
  );
}
